/**
 * D414: the guest reminder's timing. It follows the install prompt (D332): the same snooze
 * length, kept per device; dismissing it hides it for that long, and turning off "Remind me to
 * secure my account" in settings hides it for good. Only one of the two cards shows in a
 * visit: whichever would show first keeps the slot until the page is reloaded.
 */
import { useEffect, useState } from 'preact/hooks';
import { INSTALL_SNOOZE_MS } from './install.ts';

const REMIND_KEY = 'hedgerow-secure-remind';
/** Exported for the browser specs' copy in `e2e/install-key.ts`. */
export const SECURE_SNOOZE_KEY = 'hedgerow-secure-snooze-until';

function read(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // Kept for this session only.
  }
}

let remindOn = read(REMIND_KEY) !== '0';
let snoozeUntil = Number(read(SECURE_SNOOZE_KEY)) || 0;
const listeners = new Set<() => void>();

function changed(): void {
  for (const fn of listeners) fn();
}

function useStored<T>(get: () => T): T {
  const [value, setValue] = useState(get);
  useEffect(() => {
    const fn = () => setValue(() => get());
    listeners.add(fn);
    return () => void listeners.delete(fn);
  }, []);
  return value;
}

export function useSecureRemind(): [boolean, (on: boolean) => void] {
  const on = useStored(() => remindOn);
  return [
    on,
    (value) => {
      remindOn = value;
      write(REMIND_KEY, value ? '1' : '0');
      changed();
    },
  ];
}

export function useSecureSnoozedUntil(): number {
  return useStored(() => snoozeUntil);
}

export function dismissSecurePrompt(now: number): void {
  snoozeUntil = now + INSTALL_SNOOZE_MS;
  write(SECURE_SNOOZE_KEY, String(snoozeUntil));
  changed();
}

let slot: 'install' | 'secure' | null = null;

/** True when this card holds, or can take, the visit's one prompt slot. */
export function claimPromptSlot(who: 'install' | 'secure'): boolean {
  slot ??= who;
  return slot === who;
}
